import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Platform } from '@app/classes';
import { FeatureFlag } from '@app/enums';
import { FeatureGuard, PermissionGuard } from '@app/guards';
import { EmployeeModuleGuard } from '@app/guards/employee-module.guard';
import { RoutesWithOptions } from '@app/interfaces';
import { TimeTrackingSettingsView } from '@app/modules/time-tracking/views/settings/time-tracking-settings.view';
import { TimeTrackingResolver } from '@time-tracking/time-tracking.resolver';
import { TimeTrackingIndexView } from '@time-tracking/views/projects/index/time-tracking-index.view';
import { TimeTrackingShowView } from '@time-tracking/views/projects/show/time-tracking-show.view';
import { TimeTrackingStoreView } from '@time-tracking/views/projects/store/time-tracking-store.view';
import { TimeTrackingPayrollIntegration } from '@time-tracking/views/settings/payroll-integration/time-tracking-payroll-integration.view';
import { TimesheetsIndexView } from '@time-tracking/views/timesheets/index/timesheets-index.view';
import { TimeTrackingProjectGuard, TimeTrackingRedirectionGuard } from './guards';
import {
    EmployeeTimesheetStore,
    MyProjectsIndexView,
    MyProjectsShowView,
    MyTimesheetIndexView,
    MyTimesheetStoreView,
    TimeTrackingGeneralSettingsView,
    TimesheetsEditView,
    TimesheetsStoreView,
} from './views';
import { ReportsIndexView } from './views/reports/reports-index.view';

const routes: RoutesWithOptions = [
    {
        path: '',
        children: [], // TimeTrackingRedirectionGuard always redirects. Therefore, nothing is defined for this route
        pathMatch: 'full',
        canActivate: [TimeTrackingRedirectionGuard],
    },
    {
        path: 'projects',
        canActivate: [EmployeeModuleGuard, TimeTrackingProjectGuard],
        data: {
            employeeModule: Platform.modules.timeTracking,
        },
        children: [
            {
                path: '',
                component: TimeTrackingIndexView,
            },
            {
                path: 'create',
                component: TimeTrackingStoreView,
                canActivate: [PermissionGuard],
                data: {
                    permission: Platform.timeTracking.permission.view,
                },
            },
            {
                path: ':project',
                canActivate: [TimeTrackingProjectGuard], // we need this guard here too so we validate the individual project
                resolve: {
                    models: TimeTrackingResolver,
                },
                children: [
                    {
                        path: '',
                        component: TimeTrackingShowView,
                        resolve: {
                            models: TimeTrackingResolver,
                        },
                    },
                    {
                        path: 'edit',
                        component: TimeTrackingStoreView,
                        resolve: {
                            models: TimeTrackingResolver,
                        },
                    },
                ],
            },
        ],
    },
    {
        path: 'my-timesheet',
        canActivate: [EmployeeModuleGuard],
        data: {
            employeeModule: Platform.modules.timeTracking,
        },
        children: [
            {
                path: '',
                component: MyTimesheetIndexView,
            },
            {
                path: 'create',
                component: MyTimesheetStoreView,
            },
            {
                path: ':timesheetEntry',
                resolve: {
                    models: TimeTrackingResolver,
                },
                children: [
                    {
                        path: 'edit',
                        component: TimesheetsEditView,
                    },
                ],
            },
        ],
    },
    {
        path: 'my-projects',
        canActivate: [EmployeeModuleGuard],
        data: {
            employeeModule: Platform.modules.timeTracking,
        },
        children: [
            {
                path: '',
                component: MyProjectsIndexView,
            },
            {
                path: ':projectAssignment',
                resolve: {
                    models: TimeTrackingResolver,
                },
                children: [
                    {
                        path: '',
                        component: MyProjectsShowView,
                    },
                ],
            },
        ],
    },
    {
        path: 'timesheets',
        canActivate: [EmployeeModuleGuard],
        data: {
            employeeModule: Platform.modules.timeTracking,
        },
        children: [
            {
                path: '',
                component: TimesheetsIndexView,
            },
            {
                path: 'create',
                component: TimesheetsStoreView,
            },
            {
                path: ':timesheetEntry',
                resolve: {
                    models: TimeTrackingResolver,
                },
                children: [
                    {
                        path: 'edit',
                        component: TimesheetsEditView,
                    },
                ],
            },
            {
                path: 'employee',
                children: [
                    {
                        path: ':employee/create',
                        component: EmployeeTimesheetStore,
                        resolve: {
                            models: TimeTrackingResolver,
                        },
                    },
                    {
                        path: ':timesheetEntry/edit',
                        component: TimesheetsEditView,
                        resolve: {
                            models: TimeTrackingResolver,
                        },
                    },
                ],
            },
        ],
    },
    {
        path: 'reports',
        canActivate: [PermissionGuard],
        data: {
            permission: Platform.timeTracking.permission.view,
        },
        children: [
            {
                path: '',
                component: ReportsIndexView,
            },
        ],
    },
    {
        path: 'settings',
        canActivate: [PermissionGuard],
        data: {
            permission: Platform.timeTracking.permission.view,
        },
        children: [
            { path: '', component: TimeTrackingSettingsView },
            {
                path: 'general-settings',
                component: TimeTrackingGeneralSettingsView,
            },
            {
                path: 'payroll-integration',
                component: TimeTrackingPayrollIntegration,
            },
        ],
    },
];

@NgModule({
    imports: [RouterModule.forChild(routes)],
    exports: [RouterModule],
})
export class TimeTrackingRouterModule {}
